import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageIcon from "@mui/icons-material/Storage";
import { useTranslation } from "../../i18n";
import { useAdminBackups, BackupRecord } from "../../hooks/useEnvironmentBackups";
import { glass, glassTableSx } from "../../theme/glassTokens";

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function statusColor(s: string): "success" | "warning" | "error" | "info" | "default" {
  switch (s) {
    case "completed": return "success";
    case "running":   return "info";
    case "pending":   return "warning";
    case "failed":    return "error";
    default:          return "default";
  }
}

const AdminBackupsTab: React.FC = () => {
  const { t } = useTranslation();
  const { records, loading, error, refetch, triggerFullBackup } = useAdminBackups();
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMsg, setBackupMsg] = useState<string | null>(null);

  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;

  const handleFullBackup = async () => {
    try {
      setBackupLoading(true);
      setBackupMsg(null);
      const result = await triggerFullBackup();
      setBackupMsg(`Backup queued (task: ${result.task_id})`);
      setTimeout(() => refetch(), 3000);
    } catch (err: any) {
      setBackupMsg(err.response?.data?.error || "Failed to trigger backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const fullBackups = records.filter((r) => r.backup_type === "full");
  const envBackups = records.filter((r) => r.backup_type === "environment");
  const totalStorage = records.reduce((acc, r) => acc + (r.size_bytes || 0), 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
            {t("admin.backups_title", "Database Backups")}
          </Typography>
          <Typography variant="body2" sx={{ color: textSecondary }}>
            {t("admin.backups_subtitle", "Full database and environment-level backup management")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={loading}
          >
            {t("common.refresh", "Refresh")}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={backupLoading ? <CircularProgress size={16} /> : <BackupIcon />}
            onClick={handleFullBackup}
            disabled={backupLoading}
          >
            {t("admin.backup_now", "Backup Now")}
          </Button>
        </Box>
      </Box>

      {backupMsg && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setBackupMsg(null)}>
          {backupMsg}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Storage summary */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Paper sx={{ p: 2, flex: 1, minWidth: 180, background: glass.panel, border: `1px solid ${glass.panelBorder}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <StorageIcon fontSize="small" sx={{ color: textSecondary }} />
            <Typography variant="body2" sx={{ color: textSecondary }}>
              Total Storage
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary }}>
            {formatBytes(totalStorage)}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 180, background: glass.panel, border: `1px solid ${glass.panelBorder}` }}>
          <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>
            Full Backups
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary }}>
            {fullBackups.length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 180, background: glass.panel, border: `1px solid ${glass.panelBorder}` }}>
          <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>
            Environment Backups
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary }}>
            {envBackups.length}
          </Typography>
        </Paper>
      </Box>

      {/* Full-DB backup table */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ color: textPrimary, mb: 1 }}>
        {t("admin.full_backups", "Full Database Backups")}
      </Typography>
      <BackupTable records={fullBackups} loading={loading} showEnv={false} />

      {/* Environment backup table */}
      {envBackups.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: textPrimary, mb: 1, mt: 3 }}>
            {t("admin.env_backups", "Environment Backups")}
          </Typography>
          <BackupTable records={envBackups} loading={loading} showEnv />
        </>
      )}
    </Box>
  );
};

interface BackupTableProps {
  records: BackupRecord[];
  loading: boolean;
  showEnv: boolean;
}

const BackupTable: React.FC<BackupTableProps> = ({ records, loading, showEnv }) => {
  const { t } = useTranslation();
  const textSecondary = glass.textSecondary;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (records.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: textSecondary, py: 2 }}>
        No backups found.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mb: 2 }}>
      <Table size="small" sx={glassTableSx}>
        <TableHead>
          <TableRow>
            <TableCell>{t("admin.col_date", "Date")}</TableCell>
            {showEnv && <TableCell>{t("admin.col_environment", "Environment")}</TableCell>}
            <TableCell>{t("admin.col_status", "Status")}</TableCell>
            <TableCell align="right">{t("admin.col_size", "Size")}</TableCell>
            <TableCell align="right">{t("admin.col_duration", "Duration")}</TableCell>
            <TableCell align="right">{t("admin.col_patients", "Patients")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((r) => (
            <TableRow key={r.id || r.blob_name}>
              <TableCell>
                {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
              </TableCell>
              {showEnv && <TableCell>{r.environment_name || "—"}</TableCell>}
              <TableCell>
                <Chip label={r.status} color={statusColor(r.status)} size="small" />
              </TableCell>
              <TableCell align="right">{formatBytes(r.size_bytes)}</TableCell>
              <TableCell align="right">{formatDuration(r.duration_seconds)}</TableCell>
              <TableCell align="right">{r.patient_count ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AdminBackupsTab;
